import { useMemo } from "react";

import {
  CurrentSubscriptionQuery,
  Entitlement,
  useCurrentSubscription,
} from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

type Subscription = NonNullable<
  NonNullable<CurrentSubscriptionQuery["currentSubscription"]>
>;

export function useSubscription() {
  const { data, loading, error, refetch } = useCurrentSubscription();

  const subscriptionData = useMemo(() => {
    if (!data || !data.currentSubscription) {
      return null;
    }

    const { currentSubscription } = data;
    const remainingRoleplaySessions =
      calculateRemainingRoleplaySessions(currentSubscription);

    return {
      ...currentSubscription,
      remainingRoleplaySessions: remainingRoleplaySessions,
      allowRoleplaySession: remainingRoleplaySessions !== 0,
      allowCustomRoleplays: hasEntitlement(
        currentSubscription,
        Entitlement.CustomRoleplays,
      ),
    };
  }, [data]);

  return {
    subscriptionData,
    loading,
    error,
    refetch,
  };
}

function calculateRemainingRoleplaySessions(subscription: Subscription) {
  for (const entitlement of ensureNonNullArray(subscription?.entitlements)) {
    if (entitlement.entitlement === Entitlement.UnlimitedRoleplaySessions) {
      return -1;
    }
    if (entitlement.entitlement === Entitlement.RoleplaySessions) {
      const maxUsageValue = entitlement.maxUsageValue ?? 0;
      const usageCount = entitlement.usageCount ?? 0;
      return maxUsageValue - usageCount;
    }
  }
  return 0;
}

function hasEntitlement(subscription: Subscription, entitlement: Entitlement) {
  // first, get the entitlements from the subscription
  if (!subscription || !subscription.entitlements) {
    return false;
  }

  const subEntitlements = ensureNonNullArray(subscription.entitlements);
  const subEntitlement = subEntitlements.find(
    (e) => e.entitlement === entitlement,
  );

  if (!subEntitlement) return false;
  if (subEntitlement.booleanValue === true) return true;
  if (subEntitlement.booleanValue === false) return false;
  if (subEntitlement.maxUsageValue === undefined) return false;
  if (subEntitlement.maxUsageValue === 0) return false;
  if (subEntitlement.usageCount === undefined) return true;

  if ((subEntitlement.usageCount ?? 0) >= (subEntitlement.maxUsageValue ?? 0)) {
    return false;
  }

  return true;
}
